import { css } from '@emotion/react';
import { alpha } from '@mui/material/styles';

// The Chameleon theme has no amber/warning token (status.error is a red) and
// its secondaryTextColor is black (no muted grey). These are a standard
// caution/soft look, kept local, not client branding.
const WARNING_BG = '#fff3cd';
const WARNING_BORDER = '#ffc107';
const WARNING_BORDER_HOVER = '#e0a800';
const WARNING_TEXT = '#856404';
const MUTED_TEXT = '#4f4f4f';

const makeSolarPotentialStyles = ({ theme }) => {
  const { palette } = theme.mvf;
  const green = palette.component.continueButtonColor;
  const softGreenBg = alpha(green, 0.1);
  const softGreenBorder = alpha(green, 0.4);

  return {
    container: css({
      display: 'flex',
      flexDirection: 'column',
    }),
    backButton: css({ textAlign: 'left' }),
    backLink: css({
      textTransform: 'none',
      padding: 0,
      color: green,
      fontWeight: theme.mvf.typography.weight.medium,
    }),
    title: css({
      '& h6, & p': {
        fontSize: '1.5rem',
        fontWeight: 700,
        color: palette.text.primaryTextColor,
        marginBottom: 12,
      },
    }),
    subheading: css({
      fontSize: '0.875rem',
      color: MUTED_TEXT,
      lineHeight: 1.5,
      marginBottom: 8,
    }),
    banner: css({
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '16px 20px',
      borderRadius: 12,
      backgroundColor: softGreenBg,
      marginBottom: 16,
    }),
    bannerLabel: css({
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      fontSize: '0.875rem',
      fontWeight: 500,
      color: MUTED_TEXT,
    }),
    bannerAmount: css({
      fontSize: '1.5rem',
      fontWeight: 700,
      color: palette.component.continueButtonHoverColor,
      textAlign: 'right',
    }),
    infoButton: css({
      position: 'relative',
      display: 'inline-flex',
      alignItems: 'center',
      border: 'none',
      background: 'none',
      padding: 0,
      cursor: 'pointer',
      color: MUTED_TEXT,
      '&:hover': { color: green },
    }),
    tooltip: css({
      position: 'absolute',
      top: 'calc(100% + 8px)',
      left: 0,
      zIndex: 1,
      width: 280,
      padding: '12px 16px',
      borderRadius: 8,
      backgroundColor: palette.background.widgetBackgroundColor,
      border: `1px solid ${softGreenBorder}`,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      fontSize: '0.75rem',
      color: MUTED_TEXT,
      lineHeight: 1.5,
      textAlign: 'left',
    }),
    potentialCard: css({
      backgroundColor: softGreenBg,
      borderRadius: 12,
      padding: 20,
      marginBottom: 16,
    }),
    potentialTitle: css({
      fontSize: '0.875rem',
      fontWeight: 600,
      color: palette.text.primaryTextColor,
      marginBottom: 16,
    }),
    potentialGrid: css({
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: 12,
      '@media (max-width: 400px)': {
        gridTemplateColumns: 'repeat(2, 1fr)',
      },
    }),
    potentialItem: css({
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      backgroundColor: palette.background.widgetBackgroundColor,
      border: `1px solid ${softGreenBorder}`,
      borderRadius: 10,
      padding: '16px 8px',
      minHeight: 90,
    }),
    potentialIcon: css({
      color: green,
      marginBottom: 8,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }),
    potentialValue: css({
      fontSize: '1.25rem',
      fontWeight: 700,
      color: palette.text.primaryTextColor,
      lineHeight: 1.2,
    }),
    potentialLabel: css({
      fontSize: '0.6875rem',
      color: MUTED_TEXT,
      marginTop: 4,
      lineHeight: 1.2,
    }),
    pinLink: css({
      textAlign: 'left',
      marginTop: 8,
    }),
    pinLinkButton: css({
      textTransform: 'none',
      padding: 0,
      fontSize: '0.8125rem',
      fontWeight: 400,
      color: palette.component.continueButtonHoverColor,
      textDecoration: 'underline',
    }),
    continueButton: css({
      marginTop: 24,
    }),
    loading: css({
      padding: 24,
      textAlign: 'center',
    }),
  };
};

export const solarWarningPalette = {
  bg: WARNING_BG,
  border: WARNING_BORDER,
  borderHover: WARNING_BORDER_HOVER,
  text: WARNING_TEXT,
};

export const solarMutedText = MUTED_TEXT;

export default makeSolarPotentialStyles;
